/**
 * SubscribersEmptyState
 *
 * Rendered inside the Subscribers page when the current user has no
 * subscribers yet. Shows a centered explanatory message and a
 * "Share profile" CTA that triggers the user-profile share flow.
 *
 * Mirrors Flutter's empty state in
 * `lib/presentation/screens/subscriptions/subscribers_screen.dart` (the
 * bordered exclusive-color "Share profile" button). On web we use the
 * existing `secondary` variant — it's the closest match to a bordered
 * button that already exists in the design system.
 */

import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import {
  ButtonSize,
  ButtonVariant,
  ExpauseButton,
} from '@/presentation/components/common/controls/ExpauseButton';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { ShareSocialSheet } from '@/presentation/feature/content/components/sheets/shareSocial/ShareSocialSheet';
import { buildSocialShareProps } from '@/presentation/feature/share/buildSocialShareProps';
import { generateUserProfileShareDataUseCase } from '@/domain/usecases/share/generateUserProfileShareDataUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { RootState } from '@/presentation/store';

export interface SubscribersEmptyStateProps {
  screenKey: string;
}

export const SubscribersEmptyState = ({ screenKey }: SubscribersEmptyStateProps) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const handleShare = async () => {
    if (!currentUser) {
      return;
    }

    const shareContent = await generateUserProfileShareDataUseCase.execute(currentUser);
    const socialShareProps = buildSocialShareProps(currentUser, shareContent);

    // Try native Web Share API first (best on mobile, supports AirDrop on Safari)
    if (navigator.share) {
      try {
        // Open a 'protective' sheet so that, when tapping outside the
        // share modal to close, it doesn't close the underlying sheet.
        SheetService.show({
          title: '',
          content: <div />,
          container: null,
          screenKey,
          fixedHeight: '0vh',
          width: '0vw',
          closeOnClickOutside: false,
        });

        const shareData: ShareData = shareContent.file
          ? { title: shareContent.title, text: `${shareContent.text}\n\n${shareContent.shareUrl}`, files: [shareContent.file] }
          : { title: shareContent.title, text: shareContent.text, url: shareContent.shareUrl };

        await navigator.share(shareData);
        SheetService.close();
      } catch (error) {
        SheetService.close();
        const err = error instanceof Error ? error : new Error(String(error));
        if (err.message === 'Share canceled' || err.name === 'AbortError') {
          return;
        }
        appLogger.error('Error sharing user profile', 'SubscribersEmptyState.handleShare', { error: err });
        toast.error(l10n.userActions.socialShareError);
        ShareSocialSheet.show(socialShareProps, null, screenKey);
      }
    } else {
      // Browser doesn't support Web Share API - show fallback share sheet
      ShareSocialSheet.show(socialShareProps, null, screenKey);
    }
  };

  return (
    <div
      {...qaAttr({ id: 'subscribers-empty' })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: responsive.spacing.xl,
        padding: `${responsive.spacing['3xl']}px ${responsive.spacing.xl}px`,
        textAlign: 'center',
      }}
    >
      <p
        style={{
          color: theme.text.secondary,
          fontSize: responsive.font.base,
          margin: 0,
        }}
      >
        {l10n.subscribers.empty}
      </p>
      <ExpauseButton
        {...qaAttr({ id: 'subscribers-empty-share' })}
        variant={ButtonVariant.secondary}
        size={ButtonSize.medium}
        onClick={() => void handleShare()}
      >
        {l10n.subscribers.shareProfileButton}
      </ExpauseButton>
    </div>
  );
};
